import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { useGetProject } from ':cloud/hooks/useGetProject';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';

function useProjectNavigation() {
  const history = useHistory();
  const location = useLocation();
  const { activeOrg } = useGetUser();
  const { selectedProject, setSelectedProject } = useAppState();
  const { projects, isLoading: isProjectsLoading } = useGetProjects(activeOrg?.organizationId);

  const projectIdParam = new URLSearchParams(location.search).get('projectId');
  const prevProjectIdParam = useRef(projectIdParam);
  const foundProject = useGetProject(projectIdParam);

  useEffect(
    () => {
      if (!projects?.length || isProjectsLoading) return;

      const fallbackProject = projects[0];

      if (!projectIdParam) {
        const newProjectId = selectedProject?.id || fallbackProject.id;
        if (newProjectId !== projectIdParam) {
          history.replace(`${location.pathname}?projectId=${newProjectId}`);
        }
        return;
      }

      // if selected project can't be found in the user's list of projects
      // (suggests incorrect projectId param in route or user does not have permission to view that project)
      if (!foundProject) {
        if (fallbackProject.id !== projectIdParam) {
          history.replace(`${AppRoute.Projects.Overview}?projectId=${fallbackProject.id}`);
        }
      }

      if (prevProjectIdParam.current !== projectIdParam) {
        prevProjectIdParam.current = projectIdParam;

        if (foundProject && selectedProject?.id !== projectIdParam) {
          setSelectedProject(foundProject);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including projectIdParam and selectedProject causes infinite re-renders
    [isProjectsLoading, projects, foundProject, setSelectedProject, history, location.pathname],
  );
}

export default useProjectNavigation;
